import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDropdownTemplate } from "@ryde/controls";
import {
  checkCleanupNestedValidation,
  FORM_FIELD_ADDITIONAL_BUTTON_WIDTH,
  IFormAttributes,
  IValidationsYupRoot,
  Log,
  StationApi,
  useSystemParams,
} from "@ryde/shared";

export const useStationDropdownTemplate = ({
  name,
  formAttributes,
  formValidation,
}: {
  name: string;
  formAttributes: IFormAttributes | undefined | null;
  formValidation: IValidationsYupRoot | undefined | null;
}) => {
  const { t } = useTranslation();
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const {
    isAddButtonVisible,
    requiredComputed,
    isDisabled,
    isDisabledByMissingAncestor,
    disabledFormLabelTranslated,
    dropdownOptions,
    getOptionLabel,
    dropdownItems,
    handleInsertItem,
    fetchItems,
  } = useDropdownTemplate("single", name, formAttributes, formValidation);

  const { passengerStationNearByDistanceMeters } = useSystemParams();

  const { isNearbyButtonVisible, nearbyStationDependency } =
    formAttributes || {};

  const calculatedWidth = useMemo(() => {
    let width = "100%";

    if (isAddButtonVisible && isNearbyButtonVisible) {
      width = `calc(100% + ${FORM_FIELD_ADDITIONAL_BUTTON_WIDTH})`;
    }

    return width;
  }, [isAddButtonVisible, isNearbyButtonVisible]);

  const handleFindNearbyStation = useCallback(async (): Promise<void> => {
    if (nearbyStationDependency) {
      const dependantStationId = getValues(nearbyStationDependency);

      if (dependantStationId) {
        let newStationId: number | undefined;
        const passengerId = getValues("id");

        try {
          const dto = await StationApi.searchNearbyStation({
            distanceInMeters: passengerStationNearByDistanceMeters,
            stationId: dependantStationId,
            passengerId,
          });
          newStationId = dto?.id;
        } catch (error) {
          Log.errorAndToast(t("errors.nearbyAssignmentStationFailed"), error);
        }

        if (newStationId) {
          const numNewId = +newStationId;
          const currentValue = getValues(name);
          if (currentValue === numNewId) {
            Log.infoAndToast(t("general.NEARBY_STATION.ALREADY_SELECTED"));
            return;
          }
          checkCleanupNestedValidation(name, errors);
          await fetchItems(true);
          setValue(name, numNewId, {
            shouldDirty: true,
            shouldValidate: true,
          });
          Log.infoAndToast(t("general.NEARBY_STATION.SUCCESS"));
        } else {
          Log.warnAndToast(t("general.NEARBY_STATION.NOT_FOUND"));
        }
      } else {
        Log.warnAndToast(
          t("general.NEARBY_STATION.DEPENDANT_STATION_REQUIRED"),
        );
      }
    }
  }, [
    passengerStationNearByDistanceMeters,
    nearbyStationDependency,
    getValues,
    name,
    errors,
    fetchItems,
    setValue,
    t,
  ]);

  return {
    isAddButtonVisible,
    requiredComputed,
    dropdownOptions,
    getOptionLabel,
    isDisabled,
    isDisabledByMissingAncestor,
    disabledFormLabelTranslated,
    dropdownItems,
    handleInsertItem,
    calculatedWidth,
    handleFindNearbyStation,
  };
};
